/**
 * Displays Zeitgeist trending recommendations using DataTable with pagination.
 * Includes an 11-column compact table, push-style side panel for AI summaries,
 * and informational tooltips on score column headers.
 */
import React, { useState, useRef } from 'react';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Checkbox from '@amzn/meridian/checkbox';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import DateTime from '@amzn/meridian/date-time';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@amzn/meridian/data-table';
import { PageLoader } from 'src/components/common/loaders/page-loader';
import { TooltipHeader } from 'src/components/common/TooltipHeader';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import { useGetSocialSignalsRecommendationsQuery, useCatalogControllerGetCatalogTitlesMutation } from 'src/store/leela-api';
import { SocialSignalsActionBar, EMPTY_SOCIAL_SIGNALS_FILTERS, type SocialSignalsFilterState } from 'src/components/social-signals/SocialSignalsActionBar';
import { TOPIC_COLORS, PUBLISHER_COLORS } from 'src/constants';
import { useTranslation } from 'src/arb-wrapper';
import { useSideSheet } from 'src/contexts/SideSheetContext';
import { SocialSignalsDetailPanel, type SocialSignalsPanelData } from 'src/components/social-signals/SocialSignalsDetailPanel';

const PAGE_SIZE = 20;
const COLUMN_COUNT = 7;

const HEADER_STYLE: React.CSSProperties = { whiteSpace: 'normal', wordWrap: 'break-word' };

export const SocialSignals: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<SocialSignalsFilterState>(EMPTY_SOCIAL_SIGNALS_FILTERS);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending' | undefined>(undefined);
  const [trendSearch, setTrendSearch] = useState('');
  const [useUtc, setUseUtc] = useState(false);
  const { openSheet, closeSheet } = useSideSheet();
  const [fetchCatalog] = useCatalogControllerGetCatalogTitlesMutation();

  const activeTitleRef = useRef<string | null>(null);

  const renderPanel = (data: SocialSignalsPanelData) => {
    openSheet(<SocialSignalsDetailPanel data={data} onClose={closeSheet} />);
  };

  const openPanel = async (rec: typeof results[number]) => {
    const panelData: SocialSignalsPanelData = {
      titleName: rec.titleName,
      titleId: rec.titleId,
      trendText: rec.trendText,
      signalTimestamp: rec.signalTimestamp,
      trendSummary: rec.trendSummary,
      sentimentSummary: rec.sentimentSummary,
      llmReason: rec.llmReason,
      imageLoading: true,
      sourcePlatforms: rec.sourcePlatforms,
      tiktokPlatformMetadata: rec.tiktokPlatformMetadata,
      serpapiPlatformMetadata: rec.serpapiPlatformMetadata,
      trendingHistory: rec.trendingHistory,
      volumeRaw: rec.volumeRaw,
    };
    activeTitleRef.current = rec.titleId;
    renderPanel(panelData);
    try {
      const catalogResults = await fetchCatalog({ getCatalogGtiRequest: { gtiList: [rec.titleId] } }).unwrap();
      if (activeTitleRef.current !== rec.titleId) return;
      if (catalogResults.length > 0 && catalogResults[0].coverImageUrl) {
        renderPanel({ ...panelData, imageUrl: catalogResults[0].coverImageUrl, imageLoading: false });
      } else {
        renderPanel({ ...panelData, imageLoading: false });
      }
    } catch {
      if (activeTitleRef.current !== rec.titleId) return;
      renderPanel({ ...panelData, imageLoading: false });
    }
  };

  const { data: response, isLoading, isFetching } = useGetSocialSignalsRecommendationsQuery({
    page,
    pageSize,
    country: filters.country || undefined,
    sourcePlatform: filters.sourcePlatform || undefined,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    topic: filters.topic || undefined,
    sortBy: sortColumn ?? undefined,
    sortDirection: sortDirection ?? undefined,
    trendSearch: trendSearch
      ? Array.from(new TextEncoder().encode(trendSearch), (b) => b.toString(16).padStart(2, '0')).join('')
      : undefined,
  });
  const results = response?.data ?? [];
  const sortedResults = results;
  const totalResults = response?.meta?.pagination?.total ?? 0;
  const pageCount = response?.meta?.pagination?.pageCount ?? 0;
  const isLoadingPage = isFetching && !isLoading;

  const rowKey = (r: typeof results[number]) => `${r.signalId}|${r.titleId}`;

  const allSelected = sortedResults.length > 0 && sortedResults.every((r) => selectedIds.has(rowKey(r)));
  const someSelected = sortedResults.some((r) => selectedIds.has(rowKey(r)));
  const selectAllState = allSelected ? true : someSelected ? 'mixed' as const : false;

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        sortedResults.forEach((r) => next.add(rowKey(r)));
      } else {
        sortedResults.forEach((r) => next.delete(rowKey(r)));
      }
      return next;
    });
  };

  const toggleRow = (key: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const downloadCsv = () => {
    const selected = results.filter((r) => selectedIds.has(rowKey(r)));
    const headers = ['GTI', 'Trend', 'Country', 'Topic', 'Signal Time', 'Volume', 'Source Platform', 'Trending History'];
    const rows = selected.map((r) => {
      const topic = (r.trendCategories ?? r.topics)?.replace(/[[\]"]/g, '').trim() || null;
      return [r.titleId, r.trendText, r.regionCode, topic, r.signalTimestamp, formatVolume(r), parsePlatforms(r.sourcePlatforms), r.trendingHistory].map((v) => `"${String(v ?? '-').replace(/"/g, '""')}"`).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'social-signals-export.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Column alignmentHorizontal="stretch" heights="fill" spacing="400" spacingInset="none 400 500 none" width="100%">
        <Heading level={1} type="h500">
          {t('social_signals')}
        </Heading>

        <SocialSignalsActionBar
          filters={filters}
          search={trendSearch}
          selectedCount={selectedIds.size}
          onClearSelection={() => { setSelectedIds(new Set()); }}
          onDownloadCsv={downloadCsv}
          onFiltersChange={(f) => { setFilters(f); setPage(1); setSelectedIds(new Set()); }}
          onSearchChange={(v) => { setTrendSearch(v); setPage(1); setSelectedIds(new Set()); }}
        />

        {isLoading
          ? (
          <Column alignmentHorizontal="center" alignmentVertical="center" height="300px">
            <PageLoader message={t('social_signals_loading')} />
          </Column>
            )
          : (
          <Column spacing="400">
            <Row alignmentVertical="center" spacing="300" widths={['fill', 'fit']}>
              <Row alignmentVertical="center" spacing="300">
                <Text color="secondary" type="b300">{t('showing_count', { start: String(Math.min((page - 1) * pageSize + 1, totalResults)), end: String(Math.min(page * pageSize, totalResults)), total: String(totalResults) })}</Text>
                {isLoadingPage && <Loader size="small" type="linear" />}
              </Row>
              <Text color="secondary" type="b300">
                {new Date(filters.startDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                {' – '}
                {new Date(filters.endDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </Text>
            </Row>

            <div style={{ overflowX: 'auto', width: '100%' }}>
            <DataTable
              layout="fixed"
              showBorder={true}
              showColumnDividers={true}
              showRowDividers={true}
              size="medium"
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              stickyHeaderColumn={true}
              stickyHeaderRows={true}
              onSort={({ sortColumn: col, sortDirection: dir }: { sortColumn: string; sortDirection: string }) => {
                if (dir === 'none') { setSortColumn(undefined); setSortDirection(undefined); } else { setSortColumn(col); setSortDirection(dir as 'ascending' | 'descending'); }
                setPage(1);
              }}
            >
              <DataTableHead backgroundColor="secondary">
                <DataTableRow>
                  <DataTableHeader width={48}>
                    <Checkbox checked={selectAllState} data-testid="social-signals-select-all-checkbox" onChange={handleSelectAll} />
                  </DataTableHeader>
                  <DataTableHeader width={300}>
                    <TooltipHeader label={t('social_signals_col_whats_happening')} tooltip={t('social_signals_tooltip_whats_happening')} />
                  </DataTableHeader>
                  <DataTableHeader width={80}><span style={HEADER_STYLE}>{t('social_signals_col_country')}</span></DataTableHeader>
                  <DataTableHeader width={160}><span style={HEADER_STYLE}>{t('social_signals_col_topic')}</span></DataTableHeader>
                  <DataTableHeader sortColumn="signalTime" width={140}>
                    <Row alignmentVertical="center" spacing="100">
                      <span style={HEADER_STYLE}>{t('social_signals_col_signal_time')}</span>
                      <span
                        style={{ fontSize: 10, cursor: 'pointer', padding: '1px 4px', borderRadius: 3, background: '#E9EBEF' }}
                        onClick={(e) => { e.stopPropagation(); setUseUtc((v) => !v); }}
                      >
                        {useUtc ? 'UTC' : 'Local'}
                      </span>
                    </Row>
                  </DataTableHeader>
                  <DataTableHeader sortColumn="volume" width={90}>
                    <TooltipHeader label={t('social_signals_col_volume')} tooltip={t('social_signals_tooltip_volume')} />
                  </DataTableHeader>
                  <DataTableHeader width={80}><span style={HEADER_STYLE}>{t('social_signals_col_source_platform')}</span></DataTableHeader>
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {isLoadingPage
                  ? Array.from({ length: pageSize }).map((_, i) => (
                    <DataTableRow key={`skeleton-${i}`}>
                      {Array.from({ length: COLUMN_COUNT }).map((__, j) => (
                        <DataTableCell key={j}><SkeletonCell /></DataTableCell>
                      ))}
                    </DataTableRow>
                  ))
                  : (
                    <>
                      {sortedResults.length === 0 && (
                        <DataTableRow>
                          <DataTableCell colSpan={COLUMN_COUNT}>{t('table_no_results')}</DataTableCell>
                        </DataTableRow>
                      )}
                      {sortedResults.map((rec, index) => (
                        <DataTableRow key={`${rec.titleId}-${rec.signalId}-${index}`}>
                          <DataTableCell>
                            <Checkbox checked={selectedIds.has(rowKey(rec))} onChange={() => { toggleRow(rowKey(rec)); }} />
                          </DataTableCell>
                          <DataTableCell>
                            <div>
                              <Link data-testid="social-signals-title-link" type="secondary" onClick={() => { openPanel(rec); }}>{rec.trendText}</Link>
                            </div>
                          </DataTableCell>
                          <DataTableCell><Text type="b300">{rec.regionCode}</Text></DataTableCell>
                          <DataTableCell><PillList value={rec.trendCategories ?? rec.topics} /></DataTableCell>
                          <DataTableCell><DateTime dateTime={rec.signalTimestamp} format={{ month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', ...(useUtc && { timeZone: 'UTC' }) }} /></DataTableCell>
                          <DataTableCell><Text type="b300">{formatVolume(rec)}</Text></DataTableCell>
                          <DataTableCell><SourcePlatformBadges platforms={rec.sourcePlatforms} /></DataTableCell>
                        </DataTableRow>
                      ))}
                    </>
                    )}
              </DataTableBody>
            </DataTable>
            </div>

            <TablePagination
              currentPage={page}
              itemsPerPage={pageSize}
              pageCount={pageCount}
              onItemsPerPageChange={(size) => { setPageSize(size); setPage(1); setSelectedIds(new Set()); }}
              onPageChange={(p) => { setPage(p); setSelectedIds(new Set()); }}
            />
          </Column>
            )}
      </Column>
  );
};

const SkeletonCell: React.FC = () => (
  <div
    style={{
      width: '80%',
      height: '1em',
      borderRadius: 4,
      backgroundColor: '#E9EBEF',
      animation: 'pulse 1.5s ease-in-out infinite',
    }}
  />
);

const PillList: React.FC<{ value: string }> = ({ value }) => {
  if (!value) return <Text type="b300">-</Text>;
  const cleaned = value.replace(/[[\]"]/g, '');
  const items = cleaned.split(/[,|]/).map(s => s.trim()).filter(Boolean);
  if (items.length === 0) return <Text type="b300">-</Text>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, overflow: 'hidden' }}>
      {items.map((item, i) => {
        const colors = TOPIC_COLORS[item.toLowerCase()] ?? { bg: '#E9EBEF', text: '#333' };
        return (
          <span
            key={i}
            style={{
              backgroundColor: colors.bg,
              color: colors.text,
              borderRadius: 12,
              padding: '2px 8px',
              fontSize: 12,
              whiteSpace: 'nowrap',
            }}
          >
            {item.charAt(0).toUpperCase() + item.slice(1).toLowerCase()}
          </span>
        );
      })}
    </div>
  );
};

const getVolume = (rec: { tiktokPlatformMetadata?: string | null; volumeRaw?: string | null }): number => {
  let vol = 0;
  try { vol += JSON.parse(rec.tiktokPlatformMetadata ?? '{}').views_lifetime ?? 0; } catch { /* */ }
  try { vol += JSON.parse(rec.volumeRaw ?? '{}').serpapi_google_trends ?? 0; } catch { /* */ }
  return vol;
};

const formatVolume = (rec: { tiktokPlatformMetadata?: string | null; volumeRaw?: string | null }): string => {
  const vol = getVolume(rec);
  if (vol === 0) return '-';
  if (vol >= 1_000_000_000) return `${(vol / 1_000_000_000).toFixed(1)}B`;
  if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 1_000) return `${(vol / 1_000).toFixed(1)}K`;
  return String(vol);
};

const PLATFORM_LOGOS: Record<string, string> = {
  tiktok: 'TikTok',
  serpapi_google_trends: 'Google',
};

const parsePlatforms = (platforms?: string | null): string => {
  if (!platforms) return '-';
  return platforms.replace(/[[\]]/g, '').split(',').map((s) => s.trim()).filter(Boolean)
    .map((p) => PLATFORM_LOGOS[p] ?? p).join(', ');
};

const SourcePlatformBadges: React.FC<{ platforms?: string | null }> = ({ platforms }) => {
  if (!platforms) return <Text type="b300">-</Text>;
  const parsed = platforms.replace(/[[\]]/g, '').split(',').map((s) => s.trim()).filter(Boolean);
  const known = parsed.filter((p) => p in PLATFORM_LOGOS);
  if (known.length === 0) return <Text type="b300">-</Text>;
  return (
    <Column spacing="200">
      {known.map((p) => {
        const colors = PUBLISHER_COLORS[p] ?? { bg: '#F5F5F5', text: '#4A5568', accent: '#A0AEC0' };
        return (
          <span
            data-testid="source-platform-badge"
            key={p}
            style={{
              backgroundColor: colors.bg,
              color: colors.text,
              borderLeft: `3px solid ${colors.accent}`,
              borderRadius: 4,
              padding: '2px 8px',
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              display: 'inline-block',
            }}
          >
            {PLATFORM_LOGOS[p]}
          </span>
        );
      })}
    </Column>
  );
};
